import { inject, Injectable } from '@angular/core';
import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { Observable } from 'rxjs';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { PrivService } from '@api/services/priv.service';
import { Priv } from '@api/models/ClientSupportApi/Contract/priv';
import { AddPrivCategoryRequest } from '@api/models/TariffEditorApi/Contract/add-priv-category-request';
import { CategoriesOfBenefitsService } from '@api/services/categories-of-benefits.service';
import { AddPrivCategoryResponse } from '@api/models/TariffEditorApi/Contract/add-priv-category-response';
import { tap } from 'rxjs/operators';
import {
    PrivilegeConfType,
    PrivilegeConfTypeValue,
    PrivilegeIdentType,
    PrivilegeIdentTypeValue,
} from '../services/privilege/privilege-conf-ident.model';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { SetPrivCategDataRequest } from '@api/models/TariffEditorApi/Contract/set-priv-categ-data-request';
import { CustomRxState } from '@core/state/rx.state';
import { ListDoctypeResponse } from '@api/models/TariffEditorApi/Contract/list-doctype-response';


interface IPrivilegesState {
    loading: Record<StartWithUnit, boolean>;
    privileges: Priv[];
    docTypes: ListDoctypeResponse[];
}

interface PrivilegesAction {
    requestPrivileges: Priv[];
}

@Injectable()
export class PrivilegesState extends CustomRxState<IPrivilegesState> {
    readonly #privService: PrivService = inject(PrivService);
    readonly #categoriesOfBenefitsService: CategoriesOfBenefitsService = inject(CategoriesOfBenefitsService);
    readonly #actions: RxActions<PrivilegesAction> = new RxActionFactory<PrivilegesAction>().create();

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly privileges$: Observable<Priv[]> = this.select('privileges');
    public readonly docTypes$: Observable<ListDoctypeResponse[]> = this.selectLazy('docTypes', this.requestDocTypes);

    public static confList: PrivilegeConfType[] = [
        {
            code: PrivilegeConfTypeValue.None,
            name: marker('global.labels.none'),
        },
        {
            code: PrivilegeConfTypeValue.List,
            name: marker('global.labels.list'),
        },
        {
            code: PrivilegeConfTypeValue.Service,
            name: marker('global.forms.labels.service'),
        },
    ];

    public static identList: PrivilegeIdentType[] = [
        {
            code: PrivilegeIdentTypeValue.None,
            name: marker('global.labels.none'),
        },
        {
            code: PrivilegeIdentTypeValue.Doc,
            name: marker('global.labels.doc'),
        },
        {
            code: PrivilegeIdentTypeValue.Email,
            name: marker('global.forms.labels.email'),
        },
        {
            code: PrivilegeIdentTypeValue.Phone,
            name: marker('global.forms.labels.mobilePhone'),
        },
    ];

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.requestPrivileges();
    }

    public requestPrivileges(): void {
        this.hold(
            this.#privService.apiSupportListPrivCategGet().pipe(
                startWithLoading(this, StartWithUnit.RequestPrivileges),
            ),
            this.#actions.requestPrivileges,
        );
    }

    public addPrivilege(body: AddPrivCategoryRequest): Observable<AddPrivCategoryResponse> {
        return this.#categoriesOfBenefitsService.apiTariffAddPrivCategoryPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditPrivilege),
            tap(() => this.requestPrivileges()),
        );
    }

    public editPrivilege(body: SetPrivCategDataRequest): Observable<void> {
        return this.#categoriesOfBenefitsService.apiTariffSetPrivCategDataPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditPrivilege),
            tap(() => this.requestPrivileges()),
        );
    }

    private requestDocTypes(): Observable<ListDoctypeResponse[]> {
        return this.#categoriesOfBenefitsService.apiTariffListDoctypeGet();
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            privileges: null,
        });
    }

    private connectSelectors(): void {
        this.connect('privileges', this.#actions.requestPrivileges$);
    }
}
